/**
 * The dot field: where the dots are, how they move, and where they may be seen.
 *
 * Kept apart from the component because none of it is React — it is a point
 * set, a wave and a visibility rule, and all three are easier to reason about
 * (and to change) written plainly.
 *
 * **Placement.** A jittered grid, not a grid. Each dot sits in its own cell but
 * is pushed up to four tenths of a cell off centre and given its own radius, so
 * there is no row and no column for the eye to lock onto. Pure random placement
 * would have been more random and looked worse: it clumps, and clumps read as
 * dirt rather than as texture.
 *
 * **Motion.** Two waves cross the field, one long and one short, both
 * travelling with the scroll. A dot's height in that combined wave decides
 * three things at once — how far it is displaced, how wide it is drawn, and how
 * bright. Size and brightness moving with position is the whole trick: it is
 * what makes a crest read as nearer to you than a trough, rather than as a row
 * of dots that happens to be lower down.
 *
 * **Where it may be seen.** Only two places on a page have nothing written on
 * them: the margins outside the content column, and the band of empty ground
 * where one block meets the next. A dot's opacity is the larger of what those
 * two allow, and both fall off smoothly, so the field has no edge anywhere — it
 * thins out rather than stopping.
 */

/** One dot's fixed properties. Everything else is computed per frame. */
export interface Dot {
  /** Position in the tile, in CSS pixels. */
  x: number;
  y: number;
  /** Radius at rest. Varied per dot so the field has no single grain. */
  r: number;
  /** Where in the wave this dot starts, so the surface is not a clean sheet. */
  phase: number;
}

/** Roughly one dot per this many pixels each way. */
const STEP = 21;

/** How far off its cell centre a dot may sit, as a fraction of the cell. */
const JITTER = 0.42;

/** The tile is taller than the screen so the field has somewhere to drift. */
export const OVERSCAN = 340;

/**
 * A field for a viewport of this size.
 *
 * Deterministic from `seed`, so the same window always produces the same field
 * and a resize does not reshuffle every dot the reader had settled into.
 */
export function buildField(width: number, height: number, seed = 0x9e3779b9): Dot[] {
  /* A small integer hash rather than Math.random: it is stable across reloads,
     it needs no state, and a field that reshuffles itself on every resize is a
     field the reader notices. */
  let state = seed >>> 0;
  const next = () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 100000) / 100000;
  };

  const tall = height + OVERSCAN * 2;
  const cols = Math.ceil(width / STEP) + 1;
  const rows = Math.ceil(tall / STEP) + 1;

  const dots: Dot[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      dots.push({
        x: (col + 0.5) * STEP + (next() - 0.5) * 2 * JITTER * STEP,
        y: (row + 0.5) * STEP + (next() - 0.5) * 2 * JITTER * STEP,
        r: 0.72 + next() * 0.5,
        phase: next() * Math.PI * 2,
      });
    }
  }
  return dots;
}

/* ── The wave ───────────────────────────────────────────────────────────────
   A lookup table rather than Math.sin. The field is a few thousand dots and
   every one of them wants two sines a frame; on a mid-range phone that is the
   difference between this being free and this being noticeable. A thousand
   steps is finer than a pixel of displacement can show. */
const TURN = Math.PI * 2;
const LUT_SIZE = 1024;
const SINE = new Float32Array(LUT_SIZE);
for (let i = 0; i < LUT_SIZE; i += 1) SINE[i] = Math.sin((i / LUT_SIZE) * TURN);

export function sine(angle: number): number {
  const i = (angle * (LUT_SIZE / TURN)) | 0;
  return SINE[((i % LUT_SIZE) + LUT_SIZE) % LUT_SIZE];
}

/** Smoothstep. Every falloff here uses it, because a linear fade has a corner
 *  at each end and the corner is the part you see. */
export function ramp(edge0: number, edge1: number, value: number): number {
  if (edge1 === edge0) return value < edge0 ? 0 : 1;
  const t = Math.min(Math.max((value - edge0) / (edge1 - edge0), 0), 1);
  return t * t * (3 - 2 * t);
}

/** How wide the content column is. Matched to --m-container. */
export const COLUMN = 1280;

/** Clearance between the column and where dots may start. */
const COLUMN_CLEARANCE = 10;

/** Below this the margin is too thin to hold a legible field. */
const MIN_GUTTER = 34;

/** How far the seam band is held off the left and right edges of the screen,
 *  where the section rail lives and the margin field is already working. */
export function seamInset(width: number): number {
  return width >= 768 ? 96 : 24;
}

/** The margin, if this viewport has one worth using. */
export function gutter(width: number): number {
  const margin = (width - COLUMN) / 2 - COLUMN_CLEARANCE;
  return margin >= MIN_GUTTER ? margin : 0;
}
